import React from 'react';
import { Group, Circle, Star } from 'react-konva';
import Konva from 'konva';

export type Side = 'top' | 'right' | 'bottom' | 'left';

interface ConnectionTriggersProps {
  rect: { x: number; y: number; width: number; height: number };
  padSide?: number;
  padBottom?: number;
  enabledSides?: Side[];
  onStart: (side: Side, startX: number, startY: number) => void;
  onQuickCreate?: (side: Side, screenX: number, screenY: number) => void;
}

export const ConnectionTriggers: React.FC<ConnectionTriggersProps> = ({ rect, padSide = 14, padBottom = 26, enabledSides, onStart, onQuickCreate }) => {
  const midX = rect.x + rect.width / 2;
  const midY = rect.y + rect.height / 2;
  const activeSides = enabledSides ?? ['top', 'right', 'bottom', 'left'];

  const TriggerAbs: React.FC<{ x: number; y: number; side: Side }> = ({ x, y, side }) => {
    const arrowRotation = side === 'top' ? 0 : side === 'right' ? 90 : side === 'bottom' ? 180 : 270;
    let isDragging = false;
    let mouseDownTime = 0;
    let dragStarted = false;
    
    return (
      <Group
        x={x}
        y={y}
        listening
        draggable={true}
        onMouseEnter={(e) => {
          const grp = e.currentTarget as unknown as Konva.Group;
          const dot = grp.findOne('.trigger-dot') as Konva.Shape;
          const arr = grp.findOne('.trigger-arrow') as Konva.Shape;
          if ((dot as any)?.to) (dot as any).to({ scaleX: 2, scaleY: 2, duration: 0.08 });
          if ((arr as any)?.to) (arr as any).to({ opacity: 1, duration: 0.06 });
          document.body.style.cursor = 'crosshair';
        }}
        onMouseLeave={(e) => {
          const grp = e.currentTarget as unknown as Konva.Group;
          const dot = grp.findOne('.trigger-dot') as Konva.Shape;
          const arr = grp.findOne('.trigger-arrow') as Konva.Shape;
          if ((dot as any)?.to) (dot as any).to({ scaleX: 1, scaleY: 1, duration: 0.08 });
          if ((arr as any)?.to) (arr as any).to({ opacity: 0, duration: 0.06 });
          document.body.style.cursor = 'default';
        }}
        onMouseDown={(e) => {
          e.cancelBubble = true;
          isDragging = false;
          dragStarted = false;
          mouseDownTime = Date.now();
        }}
        onDragStart={(e) => {
          e.cancelBubble = true;
          isDragging = true;
          dragStarted = true;
          
          // Start connection immediately on drag
          let startX = midX, startY = midY;
          if (side === 'top') { startX = midX; startY = rect.y; }
          if (side === 'right') { startX = rect.x + rect.width; startY = midY; }
          if (side === 'bottom') { startX = midX; startY = rect.y + rect.height; }
          if (side === 'left') { startX = rect.x; startY = midY; }
          onStart(side, startX, startY);
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          // Reset position since we don't want the trigger to actually move
          const group = e.target as Konva.Group;
          group.position({ x, y });
        }}
        onMouseUp={(e) => {
          e.cancelBubble = true;
          const clickDuration = Date.now() - mouseDownTime;
          
          // Only handle click if no drag occurred
          if (!isDragging && !dragStarted && clickDuration < 300) { // Quick click
            if (onQuickCreate) {
              // Get screen coordinates for the popup
              const stage = e.target.getStage();
              if (stage) {
                const container = stage.container();
                const containerRect = container.getBoundingClientRect();
                const scale = stage.scaleX() || 1;
                const pos = stage.position();
                const screenX = containerRect.left + pos.x + x * scale;
                const screenY = containerRect.top + pos.y + y * scale;
                onQuickCreate(side, screenX, screenY);
              }
            }
          }
          
          // Reset flags
          isDragging = false;
          dragStarted = false;
        }}
      >
        <Circle radius={6} fill="#3b82f6" stroke="#ffffff" strokeWidth={2} name="trigger-dot" />
        {/* <RegularPolygon
          sides={3}
          radius={6}
          fill="#ffffff"
          stroke="#3b82f6"
          strokeWidth={1}
          rotation={arrowRotation}
          opacity={0}
          name="trigger-arrow"
        /> */}

        {/* plus create, add next shape icon replace with plus icon */} 
        <Star
          numPoints={4}
          innerRadius={0}
          outerRadius={5}
          fill="#ffffff"
          stroke="#ffffffff"
          strokeWidth={1.5}
          rotation={arrowRotation}
          opacity={0}
          name="trigger-arrow"
        />
      </Group>
    );
  };

  return (
    <Group listening>
      {activeSides.includes('top') && (
        <TriggerAbs x={midX} y={rect.y - padSide} side="top" />
      )}
      {activeSides.includes('right') && (
        <TriggerAbs x={rect.x + rect.width + padSide} y={midY} side="right" />
      )}
      {activeSides.includes('bottom') && (
        <TriggerAbs x={midX} y={rect.y + rect.height + padBottom} side="bottom" />
      )}
      {activeSides.includes('left') && (
        <TriggerAbs x={rect.x - padSide} y={midY} side="left" />
      )}
    </Group>
  );
};

